//DataTable.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
// formatDisplayValue replaced with formatFieldValue from formatters.js to support global units
import { apiFetch } from '../../api/api'; // Import API function
import './DataTable.css';
import { DataForm } from '../forms';
import { SelectableInput } from '../forms';
import { isFieldSelectable, isFieldLookup, formatFieldValue, formatFieldValueWithColumnConfig } from '../../utils/formatters';
import { getGlobalUnitSettings, resolveFieldUnitSync } from '../../utils/globalUnits';
import LookupField from '../forms/LookupField';

export default function DataTable({
  data = [],
  columns = [], // columns are expected to be columnConfig objects
  role = '',
  onEdit = null,
  onDelete = null,
  onDataChange = null, // Callback to refresh data after saves
  itemsPerPage = 5,
  title = 'Data Table',
  tableName = null, // New prop to identify which table this is
  // Filter props
  onColumnFilter = null,
  onClearColumnFilter = null,
  columnFilters = {}
}) {
  const [filterText, setFilterText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage);
  const [tableConfig, setTableConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  
  // Column resizing state
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({});
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [hasUserPreferences, setHasUserPreferences] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  
  // Row action menu state
  const [showRowActionMenu, setShowRowActionMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [showDataForm, setShowDataForm] = useState(false); // stores row index of open menu
  
  // Filter panel state
  const [showFilterPanel, setShowFilterPanel] = useState(null);
  const [filterPanelPosition, setFilterPanelPosition] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [globalSettings, setGlobalSettings] = useState({});
  
  // QuickFilter state
  const [showQuickFilter, setShowQuickFilter] = useState(false);
  const [selectedQuickFilterColumn, setSelectedQuickFilterColumn] = useState(null);
  const [quickFilterValues, setQuickFilterValues] = useState([]);
  const [isLoadingQuickFilter, setIsLoadingQuickFilter] = useState(false);
  const [selectedQuickFilterValues, setSelectedQuickFilterValues] = useState([]); // NEW: for multi-select
  
  const tableRef = useRef(null);
  const columnMenuRef = useRef(null);
  const rowActionMenuRef = useRef(null);
  const filterPanelRef = useRef(null);

  // Generate or get session ID
  useEffect(() => {
    let currentSessionId = localStorage.getItem('drillpro-session-id');
    if (!currentSessionId) {
      currentSessionId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('drillpro-session-id', currentSessionId);
    }
    setSessionId(currentSessionId);
  }, []);

  // Load global units settings
  useEffect(() => {
    const loadGlobalSettings = async () => {
      try {
        const settings = await getGlobalUnitSettings();
        if (settings) {
          setGlobalSettings(settings);
        }
      } catch (error) {
        console.error('Failed to load global unit settings:', error);
      }
    };
    loadGlobalSettings();
  }, []);

  // Load user preferences when tableName and sessionId are available
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!tableName || !sessionId) return;

      setIsLoadingPreferences(true);
      try {
        const response = await apiFetch(`/api/user-session/column-visibility/${tableName}`, {
          headers: {
            'session-id': sessionId
          }
        });

        if (response.hasUserPreferences) {
          setHasUserPreferences(true);
          // Remove lastUpdated from the preferences before setting
          const { lastUpdated, ...columnVisibility } = response.columnVisibility;
          setVisibleColumns(columnVisibility);
        } else {
          setHasUserPreferences(false);
          // Initialize with all columns visible
          if (columns.length > 0) {
            const defaultVisibility = {};
            columns.forEach(col => {
              defaultVisibility[col.name] = true;
            });
            setVisibleColumns(defaultVisibility);
          }
        }
      } catch (error) {
        console.error('Failed to load user preferences:', error);
        setHasUserPreferences(false);
        // Fall back to default visibility
        if (columns.length > 0) {
          const defaultVisibility = {};
          columns.forEach(col => {
            defaultVisibility[col.name] = true;
          });
          setVisibleColumns(defaultVisibility);
        }
      }
      setIsLoadingPreferences(false);
    };

    loadUserPreferences();
  }, [tableName, sessionId, columns]);

  // Initialize column widths when columns change
  useEffect(() => {
    if (columns.length > 0) {
      const initialWidths = {};
      
      columns.forEach((col, index) => {
        // Set default width based on column type and content
        let defaultWidth = 120; // Base width
        
        if (col.type === 'datetime' || col.type === 'date') {
          defaultWidth = 150;
        } else if (col.type === 'string' && col.name.toLowerCase().includes('name')) {
          defaultWidth = 180;
        } else if (['int', 'decimal2', 'decimal3', 'decimal4', 'currency', 'float'].includes(col.type)) {
          defaultWidth = 100;
        }
        
        initialWidths[col.name] = defaultWidth;
      });
      
      // No longer adding width for actions column since we're removing it
      
      setColumnWidths(initialWidths);

      // Only set default visibility if no user preferences are being loaded
      if (!hasUserPreferences && !isLoadingPreferences) {
        const initialVisibility = {};
        columns.forEach(col => {
          initialVisibility[col.name] = true;
        });
        setVisibleColumns(initialVisibility);
      }
    }
  }, [columns, hasUserPreferences, isLoadingPreferences]);

  // Handle clicks outside menus to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
      if (rowActionMenuRef.current && !rowActionMenuRef.current.contains(event.target)) {
        setShowRowActionMenu(null);
        setMenuPosition(null);
      }
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        setShowFilterPanel(null);
        setFilterPanelPosition(null);
      }
    };

    if (showColumnMenu || showRowActionMenu !== null || showFilterPanel !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnMenu, showRowActionMenu, showFilterPanel]);

  // Mouse event handlers for column resizing
  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !resizingColumn) {
      console.log('Mouse move ignored:', { isResizing, resizingColumn }); // Debug
      return;
    }
    
    const diff = e.clientX - startX;
    const newWidth = Math.max(60, startWidth + diff); // Minimum width of 60px
    
    console.log('Resizing column:', resizingColumn, 'to width:', newWidth); // Debug
    
    setColumnWidths(prev => {
      const updated = {
        ...prev,
        [resizingColumn]: newWidth
      };
      console.log('Updated column widths:', updated); // Debug
      return updated;
    });
  }, [isResizing, resizingColumn, startX, startWidth]);

  const handleMouseUp = useCallback(() => {
    console.log('Mouse up - stopping resize'); // Debug
    
    setIsResizing(false);
    setResizingColumn(null);
    setStartX(0);
    setStartWidth(0);
    
    // Remove global mouse event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e, columnName) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent triggering sort
    
    console.log('Mouse down on column:', columnName, 'at position:', e.clientX); // Debug log
    console.log('Current column widths:', columnWidths); // Debug
    
    const currentWidth = columnWidths[columnName] || 120;
    console.log('Starting width for', columnName, ':', currentWidth); // Debug
    
    setIsResizing(true);
    setResizingColumn(columnName);
    setStartX(e.clientX);
    setStartWidth(currentWidth);
    
    // Add global mouse event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnWidths, handleMouseMove, handleMouseUp]);

  // Clean up event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Pagination handlers
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Column visibility handlers
  const toggleColumnVisibility = (columnName) => {
    setVisibleColumns(prev => {
      const updated = {
        ...prev,
        [columnName]: !prev[columnName]
      };
      // Auto-save preferences when user makes changes
      saveUserPreferences(updated);
      return updated;
    });
  };

  const showAllColumns = () => {
    const allVisible = {};
    columns.forEach(col => {
      allVisible[col.name] = true;
    });
    setVisibleColumns(allVisible);
    saveUserPreferences(allVisible);
  };

  const hideAllColumns = () => {
    const allHidden = {};
    columns.forEach(col => {
      allHidden[col.name] = false;
    });
    setVisibleColumns(allHidden);
    saveUserPreferences(allHidden);
  };

  const resetToDefault = async () => {
    if (!tableName || !sessionId) return;

    try {
      setIsSavingPreferences(true);
      await apiFetch(`/api/user-session/column-visibility/${tableName}`, {
        method: 'DELETE',
        headers: {
          'session-id': sessionId
        }
      });

      // Reset to default (all visible)
      const defaultVisibility = {};
      columns.forEach(col => {
        defaultVisibility[col.name] = true;
      });
      setVisibleColumns(defaultVisibility);
      setHasUserPreferences(false);
    } catch (error) {
      console.error('Failed to reset preferences:', error);
    } finally {
      setIsSavingPreferences(false);
    }
  };

  const saveUserPreferences = async (columnVisibility) => {
    if (!tableName || !sessionId) return;

    try {
      setIsSavingPreferences(true);
      await apiFetch(`/api/user-session/column-visibility/${tableName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': sessionId
        },
        body: JSON.stringify({ columnVisibility })
      });
      setHasUserPreferences(true);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    } finally {
      setIsSavingPreferences(false);
    }
  };

  // Get filtered columns based on visibility
  const visibleColumnsList = columns.filter(col => visibleColumns[col.name]);

  // Fetch table configuration when tableName changes
  useEffect(() => {
    const fetchTableConfig = async () => {
      if (!tableName) {
        setTableConfig(null);
        return;
      }

      setIsLoadingConfig(true);
      try {
        const data = await apiFetch('/api/config/columns');
        if (Array.isArray(data)) {
          // Find the configuration for this specific table
          const config = data.find(tableConfig => 
            tableConfig.tableName && tableConfig.tableName.toLowerCase() === tableName.toLowerCase()
          );
          setTableConfig(config || null);
        }
      } catch (error) {
        console.error('Failed to fetch table configuration:', error);
        setTableConfig(null);
      }
      setIsLoadingConfig(false);
    };

    fetchTableConfig();
  }, [tableName]);

  // Apply custom CSS dynamically
  useEffect(() => {
    if (!tableConfig || (!tableConfig.tableCSS && !tableConfig.titleCSS)) {
      return;
    }

    // Create a unique style element for this table instance
    const styleId = `datatable-custom-style-${tableName || 'default'}`;
    let styleElement = document.getElementById(styleId);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    // Generate scoped CSS using a unique class
    const tableClass = `datatable-${tableName?.replace(/[^a-zA-Z0-9]/g, '-') || 'default'}`;
    let customCSS = '';

    // Add title CSS if available
    if (tableConfig.titleCSS) {
      const titleCSS = tableConfig.titleCSS
        .replace(/\.table-title/g, `.${tableClass} .datatable-header h3`)
        .replace(/^(?!.*\.)([^{]+)/gm, `.${tableClass} $1`); // Scope other selectors
      customCSS += titleCSS + '\n\n';
    }

    // Add table CSS if available
    if (tableConfig.tableCSS) {
      const tableCSS = tableConfig.tableCSS
        .replace(/^table/gm, `.${tableClass} .datatable-table`)
        .replace(/^th/gm, `.${tableClass} .datatable-table th`)
        .replace(/^td/gm, `.${tableClass} .datatable-table td`)
        .replace(/^tr/gm, `.${tableClass} .datatable-table tr`)
        .replace(/(\s+)table(?=\s*{)/g, `$1.${tableClass} .datatable-table`)
        .replace(/(\s+)th(?=[\s:,{])/g, `$1.${tableClass} .datatable-table th`)
        .replace(/(\s+)td(?=[\s:,{])/g, `$1.${tableClass} .datatable-table td`)
        .replace(/(\s+)tr(?=[\s:,{])/g, `$1.${tableClass} .datatable-table tr`);
      customCSS += tableCSS;
    }

    styleElement.textContent = customCSS;

    // Cleanup function to remove style when component unmounts
    return () => {
      const elementToRemove = document.getElementById(styleId);
      if (elementToRemove) {
        elementToRemove.remove();
      }
    };
  }, [tableConfig, tableName]);

  // Get display title - use displayName from config if available, otherwise use title prop
  const displayTitle = tableConfig?.displayName || title;

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }
    );
  };

  const filteredData = data.filter((row) =>
    visibleColumnsList.some((col) => {
      // For filtering, it's better to use the raw value or a simple string conversion
      const rawValue = row[col.name];
      return (rawValue === null || rawValue === undefined) ? false : String(rawValue).toLowerCase().includes(filterText.toLowerCase());
    })
  );

  const sortedData = [...filteredData].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;
    
    // Find the column config for the sort key to check its type for sorting
    const sortColumnConfig = columns.find(col => col.name === key);
    const sortType = sortColumnConfig ? sortColumnConfig.type : 'string';

    const aVal = a[key];
    const bVal = b[key];

    // Type-aware sorting
    switch (sortType) {
        case 'date':
        case 'datetime':
            const aDate = new Date(aVal).getTime();
            const bDate = new Date(bVal).getTime();
            if (isNaN(aDate) && isNaN(bDate)) return 0;
            if (isNaN(aDate)) return direction === 'asc' ? 1 : -1; // non-dates go to end/start
            if (isNaN(bDate)) return direction === 'asc' ? -1 : 1;
            return direction === 'asc' ? aDate - bDate : bDate - aDate;
        case 'int':
        case 'decimal2':
        case 'decimal3':
        case 'decimal4':
        case 'currency':
        case 'float':
        case 'comma': // Assuming comma represents a number that should be sorted numerically
            const aNum = parseFloat(aVal);
            const bNum = parseFloat(bVal);
            if (isNaN(aNum) && isNaN(bNum)) return 0;
            if (isNaN(aNum)) return direction === 'asc' ? 1 : -1;
            if (isNaN(bNum)) return direction === 'asc' ? -1 : 1;
            return direction === 'asc' ? aNum - bNum : bNum - aNum;
        case 'boolean':
            const aBool = String(aVal).toLowerCase() === 'true' || String(aVal) === '1';
            const bBool = String(bVal).toLowerCase() === 'true' || String(bVal) === '1';
            if (aBool === bBool) return 0;
            return direction === 'asc' ? (aBool ? 1 : -1) : (bBool ? 1 : -1) ;
        case 'string':
        default:
            return direction === 'asc'
            ? (aVal || '').toString().localeCompare((bVal || '').toString())
            : (bVal || '').toString().localeCompare((aVal || '').toString());
    }
  });

  const paginated = sortedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const toggleExpand = (idx) => {
    setExpandedRows(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  // Generate unique class name for this table instance
  const tableClass = `datatable-${tableName?.replace(/[^a-zA-Z0-9]/g, '-') || 'default'}`;

  // Debug: Log column widths when they change
  useEffect(() => {
    console.log('Column widths updated:', columnWidths);
  }, [columnWidths]);

  // Row action handlers
  const handleRowRightClick = (e, rowIndex) => {
    e.preventDefault(); // Prevent browser context menu
    e.stopPropagation();
    
    if (showRowActionMenu === rowIndex) {
      setShowRowActionMenu(null);
      setMenuPosition(null);
    } else {
      const menuWidth = 140;
      const menuHeight = 80;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Use mouse position for context menu
      let left = e.clientX;
      let top = e.clientY;
      
      // Adjust if menu would go off-screen horizontally
      if (left + menuWidth > viewportWidth - 10) {
        left = viewportWidth - menuWidth - 10;
      }
      if (left < 10) {
        left = 10;
      }
      
      // Adjust if menu would go off-screen vertically
      if (top + menuHeight > viewportHeight - 10) {
        top = viewportHeight - menuHeight - 10;
      }
      if (top < 10) {
        top = 10;
      }
      
      setMenuPosition({ top, left });
      setShowRowActionMenu(rowIndex);
    }
  };

  const handleRowEdit = (row) => {
    const rowIndex = paginated.findIndex(r => r === row);
    setEditingRow(rowIndex);
    setEditingData({ ...row });
    // Keep the menu visible - don't need to reposition since it's already showing
  };

  const handleOpenFullForm = () => {
    // Set the current row data for editing
    if (showRowActionMenu !== null && paginated[showRowActionMenu]) {
      setEditingData({ ...paginated[showRowActionMenu] });
    }
    setShowDataForm(true);
    setShowRowActionMenu(null);
    setMenuPosition(null);
  };

  const handleRowDelete = (row) => {
    setShowRowActionMenu(null);
    setMenuPosition(null);
    if (onDelete) onDelete(row);
  };

  const handleInlineEditChange = (columnName, value) => {
    setEditingData(prev => ({ ...prev, [columnName]: value }));
  };

  const handleSaveInlineEdit = async () => {
    try {
      if (tableName && editingData) {
        // Find the primary key field (usually ends with 'ID')
        const primaryKey = Object.keys(editingData).find(key => 
          key.toLowerCase().includes('id') && editingData[key]
        ) || Object.keys(editingData)[0];
        
        const primaryKeyValue = editingData[primaryKey];
        
        if (primaryKeyValue) {
          // Use universalDb endpoint to save
          const response = await apiFetch(`/api/db/${tableName}/${primaryKeyValue}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(editingData)
          });
          
          console.log('Data saved successfully:', response);
          
          // Call parent onEdit if provided (for updating local state)
          // if (onEdit) {
          //   await onEdit(editingData);
          // }
          
          // Refresh table data
          if (onDataChange) {
            await onDataChange();
          }
        } else {
          throw new Error('Primary key not found for update');
        }
      }
      
      setEditingRow(null);
      setEditingData({});
      setShowRowActionMenu(null);
      setMenuPosition(null);
    } catch (error) {
      console.error('Failed to save inline edit:', error);
      // Keep editing mode active if save fails
    }
  };

  const handleCancelInlineEdit = () => {
    setEditingRow(null);
    setEditingData({});
    setShowRowActionMenu(null);
    setMenuPosition(null);
  };

  // Column filter handlers
  const handleColumnRightClick = (e, column) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.target.getBoundingClientRect();
    setFilterPanelPosition({
      x: rect.left,
      y: rect.bottom + 5
    });
    setShowFilterPanel(column.name);
    
    // Initialize filter value if not set
    if (!filterValues[column.name]) {
      setFilterValues(prev => ({
        ...prev,
        [column.name]: {
          type: getFilterType(column),
          value: '',
          operator: '='
        }
      }));
    }
  };

  const getFilterType = (column) => {
    const type = column.type?.toLowerCase();
    if (['int', 'decimal2', 'decimal3', 'decimal4', 'currency', 'float', 'comma'].includes(type)) {
      return 'numeric';
    } else if (['date', 'datetime'].includes(type)) {
      return 'date';
    }
    return 'text';
  };

  const handleApplyFilter = (columnName) => {
    const filterConfig = filterValues[columnName];
    if (!filterConfig?.value) return;

    if (onColumnFilter) {
      onColumnFilter(columnName, filterConfig);
    }
    
    setShowFilterPanel(null);
    setFilterPanelPosition(null);
  };

  const handleClearFilter = (columnName) => {
    if (onClearColumnFilter) {
      onClearColumnFilter(columnName);
    }
    
    setFilterValues(prev => {
      const newValues = { ...prev };
      delete newValues[columnName];
      return newValues;
    });
    
    setShowFilterPanel(null);
    setFilterPanelPosition(null);
  };

  const updateFilterValue = (columnName, key, value) => {
    setFilterValues(prev => ({
      ...prev,
      [columnName]: {
        ...prev[columnName],
        [key]: value
      }
    }));
  };

  // QuickFilter functions
  const handleQuickFilterClick = () => {
    setShowQuickFilter(!showQuickFilter);
    if (!showQuickFilter) {
      setSelectedQuickFilterColumn(null);
      setQuickFilterValues([]);
      setSelectedQuickFilterValues([]); // Reset multi-select values
    }
  };

  const handleQuickFilterColumnSelect = async (column) => {
    setSelectedQuickFilterColumn(column);
    setIsLoadingQuickFilter(true);
    
    try {
      // Get unique values for the selected column
      const uniqueValues = [...new Set(data.map(row => row[column.name]).filter(val => val !== null && val !== undefined && val !== ''))];
      
      // Sort values appropriately based on column type
      const sortedValues = uniqueValues.sort((a, b) => {
        const colType = column.type ? column.type.toLowerCase() : 'string';
        
        switch (colType) {
          case 'int':
          case 'decimal2':
          case 'decimal3':
          case 'decimal4':
          case 'currency':
          case 'float':
          case 'comma':
            return parseFloat(a) - parseFloat(b);
          case 'date':
          case 'datetime':
            return new Date(a) - new Date(b);
          default:
            return String(a).localeCompare(String(b));
        }
      });
      
      setQuickFilterValues(sortedValues);
    } catch (error) {
      console.error('Failed to get unique values for quick filter:', error);
      setQuickFilterValues([]);
    } finally {
      setIsLoadingQuickFilter(false);
    }
  };

  const handleQuickFilterValueClick = (value, event) => {
    if (!selectedQuickFilterColumn) return;
    let newSelectedValues = [];
    const isCtrl = event && (event.ctrlKey || event.metaKey);
    if (isCtrl) {
      // Multi-select: add/remove value
      if (selectedQuickFilterValues.includes(value)) {
        newSelectedValues = selectedQuickFilterValues.filter(v => v !== value);
      } else {
        newSelectedValues = [...selectedQuickFilterValues, value];
      }
    } else {
      // Single select
      newSelectedValues = [value];
    }
    setSelectedQuickFilterValues(newSelectedValues);
    if (onColumnFilter) {
      const filterConfig = {
        type: 'multi',
        value: newSelectedValues,
        operator: 'in',
      };
      onColumnFilter(selectedQuickFilterColumn.name, filterConfig);
    }
  };

  const clearQuickFilter = () => {
    setSelectedQuickFilterValues([]);
    if (selectedQuickFilterColumn && onClearColumnFilter) {
      onClearColumnFilter(selectedQuickFilterColumn.name);
    }
  };

  // Get selectable columns for QuickFilter
  const getSelectableColumns = () => {
    return columns.filter(col => {
      // Include columns that are explicitly marked as selectable
      if (col.options && Array.isArray(col.options)) return true;
      if (isFieldSelectable(col)) return true;
      if (isFieldLookup(col)) return true;
      
      // Include columns with limited unique values (good for filtering)
      const uniqueValues = [...new Set(data.map(row => row[col.name]).filter(val => val !== null && val !== undefined && val !== ''))];
      return uniqueValues.length <= 20 && uniqueValues.length > 1; // Max 20 unique values, at least 2
    });
  };



  return (
    <div className={`datatable-container ${tableClass}`}>
      <div className="datatable-header">
        <div className="datatable-title-section">
        <h3>{displayTitle}</h3>
          <span className="record-count">({data.length} record{data.length !== 1 ? 's' : ''}) <small>righ-click for editing</small></span>
        </div>
        <div className="datatable-controls">
          {/* Clear Filters Button - only show if there are active filters */}
          {Object.keys(columnFilters).length > 0 && (
            <button
              className="clear-all-filters-btn"
              onClick={() => {
                // Clear all column filters
                Object.keys(columnFilters).forEach(columnName => {
                  if (onClearColumnFilter) {
                    onClearColumnFilter(columnName);
                  }
                });
                // Clear search text
                setFilterText('');
              }}
              title="Clear all filters and search"
            >
              🗑️ Clear All
            </button>
          )}
          
          <button
            className={`quick-filter-btn ${showQuickFilter ? 'active' : ''}`}
            onClick={handleQuickFilterClick}
            title="Quick Filter - Select column and click values to filter"
          >
            🔍 Quick Filter
          </button>
          
          <input
            type="text"
            placeholder="Search..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="datatable-search"
          />
          
          <div className="column-visibility-container" ref={columnMenuRef}>
            <button
              className={`column-visibility-btn ${hasUserPreferences ? 'has-custom-settings' : ''} ${isLoadingPreferences ? 'loading' : ''}`}
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              title={hasUserPreferences ? "Custom column settings applied" : "Using default column settings"}
              disabled={isLoadingPreferences}
            >
              {isLoadingPreferences ? '⏳' : hasUserPreferences ? '⚙️⭐' : '⚙️'}
            </button>
            {showColumnMenu && (
              <div className="column-visibility-menu">
                <div className="column-menu-header">
                  <h4>Column Visibility</h4>
                  <div className="column-menu-status">
                    {hasUserPreferences ? (
                      <span className="status-custom" title="Using your custom settings">
                        ⭐ Custom
                      </span>
                    ) : (
                      <span className="status-default" title="Using default settings">
                        🔄 Default
                      </span>
                    )}
                  </div>
                </div>
                <div className="column-menu-actions">
                  <button
                    className="btn-small"
                    onClick={showAllColumns}
                    title="Show All Columns"
                    disabled={isSavingPreferences}
                  >
                    All
                  </button>
                  <button
                    className="btn-small"
                    onClick={hideAllColumns}
                    title="Hide All Columns"
                    disabled={isSavingPreferences}
                  >
                    None
                  </button>
                  {hasUserPreferences && (
                    <button
                      className="btn-small btn-reset"
                      onClick={resetToDefault}
                      title="Reset to Default Settings"
                      disabled={isSavingPreferences}
                    >
                      {isSavingPreferences ? '⏳' : '🔄'}
                    </button>
                  )}
                </div>
                <div className="column-menu-section">
                  <div className="rows-per-page-section">
                    <label className="rows-per-page-label">
                      <span>Rows per page:</span>
                      <select 
                        value={rowsPerPage} 
                        onChange={(e) => handleRowsPerPageChange(parseInt(e.target.value))}
                        className="rows-per-page-select"
                        disabled={isSavingPreferences}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={data.length}>All ({data.length})</option>
                      </select>
                    </label>
                  </div>
                </div>
                <div className="column-menu-list">
                  {columns.map((col) => (
                    <label key={col.name} className="column-menu-item">
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.name] || false}
                        onChange={() => toggleColumnVisibility(col.name)}
                        disabled={isSavingPreferences}
                      />
                      <span className="column-name">{col.label || col.name}</span>
                    </label>
                  ))}
                </div>
                <div className="column-menu-footer">
                  <div className="footer-stats">
                    <small>{visibleColumnsList.length} of {columns.length} columns visible</small>
                  </div>
                  {isSavingPreferences && (
                    <div className="footer-saving">
                      <small>💾 Saving preferences...</small>
                    </div>
                  )}
                  {sessionId && (
                    <div className="footer-session">
                      <small title={`Session ID: ${sessionId}`}>Session: {sessionId.slice(-8)}</small>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* QuickFilter Panel */}
      {showQuickFilter && (
        <div className="quick-filter-panel">
          <div className="quick-filter-header">
            <h4>🔍 Quick Filter</h4>
            <button 
              className="close-quick-filter-btn"
              onClick={handleQuickFilterClick}
              title="Close Quick Filter"
            >
              ✕
            </button>
          </div>
          
          <div className="quick-filter-content">
            {!selectedQuickFilterColumn ? (
              <div className="column-selection">
                <h5>Select a column to filter:</h5>
                <div className="selectable-columns">
                  {getSelectableColumns().map((col) => {
                    const uniqueCount = [...new Set(data.map(row => row[col.name]).filter(val => val !== null && val !== undefined && val !== ''))].length;
                    return (
                      <button
                        key={col.name}
                        className="column-select-btn"
                        onClick={() => handleQuickFilterColumnSelect(col)}
                        title={`${col.label || col.name} (${uniqueCount} unique values)`}
                      >
                        <span className="column-name">{col.label || col.name}</span>
                        <span className="value-count">({uniqueCount})</span>
                      </button>
                    );
                  })}
                </div>
                {getSelectableColumns().length === 0 && (
                  <div className="no-selectable-columns">
                    <p>No suitable columns found for quick filtering.</p>
                    <p><small>Columns need to have 2-20 unique values to be available for quick filtering.</small></p>
                  </div>
                )}
              </div>
            ) : (
                             <div className="value-selection">
  <div className="quickfilter-row-compact">
    <button 
      className="back-to-columns-btn"
      onClick={() => setSelectedQuickFilterColumn(null)}
      title="Back to column selection"
    >
      ← Back
    </button>
    <span className="quickfilter-label">Filter by: {selectedQuickFilterColumn.label || selectedQuickFilterColumn.name}</span>
    <div className="values-container-inline">
      {quickFilterValues.map((value, index) => {
        const isActive = selectedQuickFilterValues.includes(value);
        return (
          <button
            key={index}
            className={`filter-value-btn ${isActive ? 'active' : ''}`}
            onClick={e => handleQuickFilterValueClick(value, e)}
            title={`Filter by: ${value}`}
          >
            {formatFieldValueWithColumnConfig(value, selectedQuickFilterColumn, globalSettings)}
          </button>
        );
      })}
    </div>
    {columnFilters[selectedQuickFilterColumn.name] && (
      <button 
        className="clear-filter-btn"
        onClick={clearQuickFilter}
        title="Clear current filter"
      >
        Clear Filter
      </button>
    )}
  </div>
  {isLoadingQuickFilter && (
    <div className="loading-values">
      <span>⏳ Loading values...</span>
    </div>
  )}
  <div className="filter-stats">
    <small>
      {selectedQuickFilterValues.length} selected • 
      {columnFilters[selectedQuickFilterColumn.name] ? ' Filtered' : ' No filter applied'}
    </small>
  </div>
</div>
            )}
          </div>
        </div>
      )}
      
      <table className={`datatable-table ${isResizing ? 'resizing' : ''}`} ref={tableRef}>
        <thead>
          <tr>
            {visibleColumnsList.map((col) => {
              const hasFilter = columnFilters[col.name];
              return (
              <th 
                key={col.name} 
                onClick={() => handleSort(col.name)}
                  onContextMenu={(e) => handleColumnRightClick(e, col)}
                  className={hasFilter ? 'filtered-column' : ''}
                style={{ 
                  width: `${columnWidths[col.name] || 120}px`,
                  minWidth: `${columnWidths[col.name] || 120}px`,
                  maxWidth: `${columnWidths[col.name] || 120}px`,
                  position: 'relative',
                    cursor: 'pointer',
                    backgroundColor: hasFilter ? '#e3f2fd' : undefined,
                    borderColor: hasFilter ? '#2196f3' : undefined
                }}
                  title={hasFilter ? `Right-click to clear filter: ${hasFilter.value}` : 'Right-click to filter'}
              >
                <div className="th-content">
                  <span className="column-label">{col.label || col.name}</span>
                  {/* Display unit only if not a currency type, as currency formatter includes symbol */}
                  {col.unit && col.type && col.type.toLowerCase() !== 'currency' && 
                    <span className="unit-label">({resolveFieldUnitSync(col, globalSettings) || col.unit})</span>
                  }
                  {col.desc && <span className="tooltip">{col.desc}</span>}
                    {hasFilter && (
                      <span className="filter-indicator" title={`Filtered by: ${hasFilter.value}`}>
                        🔍
                      </span>
                    )}
                  {sortConfig.key === col.name && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' 🔼' : ' 🔽'}
                    </span>
                  )}
                </div>
                <div 
                  className="resize-handle"
                  onMouseDown={(e) => handleMouseDown(e, col.name)}
                  onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking resize handle
                />
              </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {paginated.map((row, rowIndex) => { // Changed idx to rowIndex for clarity
            const isExpanded = expandedRows.includes(rowIndex);
            // Use a unique key for the row if available (e.g., row.id), otherwise fallback to rowIndex
            const rowKey = row.id || row.WellID || rowIndex; 
            return (
              <tr 
                key={rowKey} 
                className={`row-card ${isExpanded ? 'expanded' : ''}`} 
                onClick={() => toggleExpand(rowIndex)}
                onContextMenu={(e) => handleRowRightClick(e, rowIndex)}
                style={{ cursor: 'context-menu' }}
              >
                {visibleColumnsList.map((col, colIndex) => { // Changed i to colIndex
                  const isFirstCol = colIndex === 0;

                  if (!isFirstCol && window.innerWidth <= 768 && !isExpanded) return null;
                  
                  // Determine cell class based on type for potential styling (e.g., text-align)
                  let cellClassName = '';
                  const colType = col.type ? col.type.toLowerCase() : '';
                  if (['int', 'decimal2', 'decimal3', 'decimal4', 'currency', 'float', 'comma'].includes(colType)) {
                    cellClassName = 'numeric';
                  }

                  // Add first-column class for styling (but not when editing)
                  if (isFirstCol && editingRow !== rowIndex) {
                    cellClassName += ' first-column';
                  }

                  return (
                    <td 
                      key={col.name} 
                      data-label={col.label || col.name} 
                      className={cellClassName}
                      style={{ 
                        width: `${columnWidths[col.name] || 120}px`,
                        minWidth: `${columnWidths[col.name] || 120}px`,
                        maxWidth: `${columnWidths[col.name] || 120}px`,
                        position: isFirstCol ? 'relative' : 'unset'
                      }}
                    >
                      <div className="cell-content">
                        {editingRow === rowIndex ? (
                          // Render appropriate input field for editing based on column type
                          (() => {
                            const colType = col.type ? col.type.toLowerCase() : 'text';
                            const value = editingData[col.name] || '';
                            
                            if (col.options && Array.isArray(col.options)) {
                              // Render select dropdown for columns with options
                              return (
                                <select
                                  value={value}
                                  onChange={(e) => handleInlineEditChange(col.name, e.target.value)}
                                  className="inline-edit-select"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="">Select...</option>
                                  {col.options.map((option, idx) => (
                                    <option key={idx} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              );
                            } else if (isFieldLookup(col)) {
                              // Render LookupField for lookup columns
                              return (
                                <LookupField
                                  field={col}
                                  value={value}
                                  onChange={(newValue) => handleInlineEditChange(col.name, newValue)}
                                  className="inline-edit-input"
                                  placeholder={`Select ${col.displayName || col.label || col.name}...`}
                                  disabled={false}
                                />
                              );
                            } else if (['selectable', 'textselectable'].includes(col.type) || isFieldSelectable(col)) {
                              // Render SelectableInput for columns that should have auto-complete
                              return (
                                <SelectableInput
                                  value={value}
                                  onChange={(newValue) => handleInlineEditChange(col.name, newValue)}
                                  className="inline-edit-selectable"
                                  tableName={tableName}
                                  columnName={col.name}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              );
                            } else if (colType === 'textarea') {
                              // Render textarea for multi-line text
                              return (
                                <textarea
                                  value={value}
                                  onChange={(e) => handleInlineEditChange(col.name, e.target.value)}
                                  className="inline-edit-textarea"
                                  rows={3}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              );
                            } else if (colType === 'date') {
                              // Render date input
                              return (
                                <input
                                  type="date"
                                  value={value ? value.split('T')[0] : ''}
                                  onChange={(e) => handleInlineEditChange(col.name, e.target.value)}
                                  className="inline-edit-input"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              );
                            } else if (['int', 'decimal2', 'decimal3', 'decimal4', 'float'].includes(colType)) {
                              // Render number input
                              return (
                                <input
                                  type="number"
                                  value={value}
                                  onChange={(e) => handleInlineEditChange(col.name, e.target.value)}
                                  className="inline-edit-input"
                                  step={colType === 'int' ? '1' : 'any'}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              );
                            } else {
                              // Render text input for everything else
                              return (
                                <input
                                  type="text"
                                  value={value}
                                  onChange={(e) => handleInlineEditChange(col.name, e.target.value)}
                                  className="inline-edit-input"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              );
                            }
                          })()
                        ) : (
                          // Render normal content
                          col.link ? (() => {
                            // Ensure link generation is robust
                            const linkTemplate = String(col.link);
                            let finalLink = '#';
                            const match = linkTemplate.match(/:([a-zA-Z0-9_]+)/);
                            if (match && match[1]) {
                              const keyFromLink = match[1];
                              const valueForLink = row[keyFromLink];
                              if (valueForLink !== undefined && valueForLink !== null) {
                                finalLink = linkTemplate.replace(`:${keyFromLink}`, encodeURIComponent(valueForLink));
                              } 
                            }
                            // If no match or no value, link remains '#' or could be the static part of linkTemplate
                            return <a href={finalLink}>{formatFieldValueWithColumnConfig(row[col.name], col, globalSettings)}</a>;
                          })() : (
                            formatFieldValueWithColumnConfig(row[col.name], col, globalSettings)
                          )
                        )}
                      </div>
                      
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      
      <div className="datatable-pagination">
        page :
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            className={currentPage === i + 1 ? 'active' : ''}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </button>
        ))}
      </div>
      
      {/* Context Menu */}
      {showRowActionMenu !== null && menuPosition && (onEdit || onDelete) && (
        <div 
          className="row-action-menu"
          ref={rowActionMenuRef}
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`
          }}
        >
          <div className="row-action-header">
            {visibleColumnsList[0] && paginated[showRowActionMenu] && (
              <span className="row-identifier">
                {formatFieldValueWithColumnConfig(paginated[showRowActionMenu][visibleColumnsList[0].name], visibleColumnsList[0], globalSettings)}
              </span>
            )}
          </div>
          {onEdit && (role === 'admin' || role === 'read-write') && (
            <>
              <button
                className="row-action-item"
                onClick={() => handleRowEdit(paginated[showRowActionMenu])}
              >
                ✏️ Edit Inline
              </button>
              <button
                className="row-action-item"
                onClick={handleOpenFullForm}
              >
                📝 Edit in Full Form
              </button>
              {editingRow === showRowActionMenu && (
                <>
                  <div className="menu-separator"></div>
                  <button
                    className="row-action-item save-action"
                    onClick={handleSaveInlineEdit}
                  >
                    ✅ Save Changes
                  </button>
                  <button
                    className="row-action-item cancel-action"
                    onClick={handleCancelInlineEdit}
                  >
                    ❌ Cancel Edit
                  </button>
                </>
              )}
            </>
          )}
          {onDelete && role === 'admin' && (
            <button
              className="row-action-item row-action-delete"
              onClick={() => handleRowDelete(paginated[showRowActionMenu])}
            >
              🗑️ Delete
            </button>
          )}
        </div>
      )}
      
      {/* DataForm Modal */}
      {showDataForm && editingData && (
        <DataForm
          data={editingData}
          tableName={tableName}
          title={`Edit ${tableName || 'Data'}`}
          onSave={async (data) => {
            try {
                if (tableName && data) {
                    const primaryKey = Object.keys(data).find(key => 
                        key.toLowerCase().includes('id') && data[key]
                    ) || Object.keys(data)[0];
                    
                    const primaryKeyValue = data[primaryKey];

                    if (primaryKeyValue) {
                        const response = await apiFetch(`/api/db/${tableName}/${primaryKeyValue}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(data)
                        });
                        console.log('Data saved successfully via full form:', response);

                        if (onDataChange) {
                            await onDataChange();
                        }
                        
                        setShowDataForm(false);
                        setEditingRow(null);
                        setEditingData({});
                    } else {
                        throw new Error('Primary key not found for update');
                    }
                }
            } catch (error) {
                console.error('Failed to save via full form:', error);
                // Optionally, show an error to the user
            }
          }}
          onCancel={() => {
            setShowDataForm(false);
            setEditingData({});
            setEditingRow(null);
          }}
        />
      )}

      {/* Filter Panel */}
      {showFilterPanel && filterPanelPosition && (
        <div 
          className="filter-panel"
          ref={filterPanelRef}
          style={{
            position: 'fixed',
            top: `${filterPanelPosition.y}px`,
            left: `${filterPanelPosition.x}px`,
            zIndex: 1000
          }}
        >
          <div className="filter-panel-header">
            <h4>Filter: {columns.find(c => c.name === showFilterPanel)?.label || showFilterPanel}</h4>
            {columnFilters[showFilterPanel] && (
              <button 
                className="clear-filter-btn"
                onClick={() => handleClearFilter(showFilterPanel)}
                title="Clear current filter"
              >
                ✕
              </button>
            )}
          </div>
          
          <div className="filter-panel-content">
            {(() => {
              const column = columns.find(c => c.name === showFilterPanel);
              const filterType = getFilterType(column);
              const currentFilter = filterValues[showFilterPanel] || { type: filterType, value: '', operator: '=' };
              
              if (filterType === 'numeric') {
                return (
                  <div className="numeric-filter">
                    <div className="filter-row">
                      <select 
                        value={currentFilter.operator || '='} 
                        onChange={(e) => updateFilterValue(showFilterPanel, 'operator', e.target.value)}
                        className="operator-select"
                      >
                        <option value="=">=</option>
                        <option value="<">&lt;</option>
                        <option value=">">&gt;</option>
                        <option value="<=">&le;</option>
                        <option value=">=">&ge;</option>
                      </select>
                      <input
                        type="number"
                        placeholder="Enter number..."
                        value={currentFilter.value}
                        onChange={(e) => updateFilterValue(showFilterPanel, 'value', e.target.value)}
                        className="filter-input"
                        step="any"
                      />
                    </div>
                  </div>
                );
              } else if (filterType === 'date') {
                return (
                  <div className="date-filter">
                    <div className="filter-row">
                      <select 
                        value={currentFilter.operator || 'exact'} 
                        onChange={(e) => updateFilterValue(showFilterPanel, 'operator', e.target.value)}
                        className="operator-select"
                      >
                        <option value="exact">Exact Date</option>
                        <option value="year">Year (YYYY)</option>
                        <option value="month">Year-Month (YYYY-MM)</option>
                        <option value="range">Date Range</option>
                      </select>
                    </div>
                    <div className="filter-row">
                      {currentFilter.operator === 'range' ? (
                        <div className="date-range">
                          <input
                            type="date"
                            placeholder="Start date"
                            value={currentFilter.value.split(' to ')[0] || ''}
                            onChange={(e) => {
                              const endDate = currentFilter.value.split(' to ')[1] || '';
                              updateFilterValue(showFilterPanel, 'value', `${e.target.value} to ${endDate}`);
                            }}
                            className="filter-input"
                          />
                          <span>to</span>
                          <input
                            type="date"
                            placeholder="End date"
                            value={currentFilter.value.split(' to ')[1] || ''}
                            onChange={(e) => {
                              const startDate = currentFilter.value.split(' to ')[0] || '';
                              updateFilterValue(showFilterPanel, 'value', `${startDate} to ${e.target.value}`);
                            }}
                            className="filter-input"
                          />
                        </div>
                      ) : currentFilter.operator === 'year' ? (
                        <input
                          type="text"
                          placeholder="2023"
                          value={currentFilter.value}
                          onChange={(e) => updateFilterValue(showFilterPanel, 'value', e.target.value)}
                          className="filter-input"
                          pattern="[0-9]{4}"
                          maxLength="4"
                        />
                      ) : currentFilter.operator === 'month' ? (
                        <input
                          type="text"
                          placeholder="2023-06"
                          value={currentFilter.value}
                          onChange={(e) => updateFilterValue(showFilterPanel, 'value', e.target.value)}
                          className="filter-input"
                          pattern="[0-9]{4}-[0-9]{2}"
                          maxLength="7"
                        />
                      ) : (
                        <input
                          type="date"
                          value={currentFilter.value}
                          onChange={(e) => updateFilterValue(showFilterPanel, 'value', e.target.value)}
                          className="filter-input"
                        />
                      )}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="text-filter">
                    <div className="filter-row">
                      <select 
                        value={currentFilter.type || 'text'} 
                        onChange={(e) => updateFilterValue(showFilterPanel, 'type', e.target.value)}
                        className="operator-select"
                      >
                        <option value="text">Contains</option>
                        <option value="exact">Exact Match</option>
                        <option value="wildcard">Wildcard (* or %)</option>
                      </select>
                    </div>
                    <div className="filter-row">
                      <input
                        type="text"
                        placeholder={currentFilter.type === 'wildcard' ? 'Use * or % as wildcards' : 'Enter text...'}
                        value={currentFilter.value}
                        onChange={(e) => updateFilterValue(showFilterPanel, 'value', e.target.value)}
                        className="filter-input"
                      />
                    </div>
                  </div>
                );
              }
            })()}
          </div>
          
          <div className="filter-panel-actions">
            <button 
              className="apply-filter-btn"
              onClick={() => handleApplyFilter(showFilterPanel)}
              disabled={!filterValues[showFilterPanel]?.value}
            >
              Apply Filter
            </button>
            <button 
              className="cancel-filter-btn"
              onClick={() => {
                setShowFilterPanel(null);
                setFilterPanelPosition(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
